import { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { useFormikContext } from 'formik';

import {
  AddPlacementNameStep,
  ConfirmRelocationStep,
  ConfirmSwapStation,
  PinCoordinatesStep,
  ScanQRStep,
  SelectActionStep,
  SelectDistributionChannelStep,
  SelectExistingPlacementStep,
  SelectPlacementTypeStep,
  SelectStationStep,
  UploadPhotosStep,
} from './assign-station';

import { ATTACHMENT_FIELD_NAME, STATION_TYPE, USER_ROLE } from '~common';
import { PaddingBox } from '~components/atoms/custom';
import { IPreviewFile } from '~hooks/upload-file';
import {
  GeolocationProvider,
  PINNING_STEP,
  stepButtonLabelMap,
  usePinningStepContext,
  useVenuePlacementCreationContext,
} from '~providers';
import { getNaturalPreviousStep } from '~providers/PinningStepProvider/utils';
import { IFormCreationValues } from '~providers/useForm';
import {
  DistributionChannel,
  ICoordinate,
  PlacementType,
  StationFilterOption,
  StationPlacementType,
} from '~types';
import { checkRolesMatchWithPrimaryUserRole } from '~utils';
import { getStationInfoDisplay } from '~utils/station.util';

export const AssignStationStep = () => {
  const {
    pinningStep,
    goToNextStep,
    selectedVenueId,
    selectedPlacementId,
    existingPlacements,
    onSearchExistingPlacement,
    isLoadingPlacements,
    assignedStationImei,
    assignedStationId,
    assignedStationType,
    isRelocationFlow,
    isAddingFromReview,
  } = usePinningStepContext();

  const {
    existingVenueDetail,
    existingVenueCoordinates,
    handleUpdatePinningFormData,
    distributionChannelOptions,
    placementListInfo,
  } = useVenuePlacementCreationContext();

  const [currentFields, setCurrentFields] = useState<string[]>([]);
  const [hasPinMoved, setHasPinMoved] = useState<boolean>(false);

  const { values, errors, setFieldValue } =
    useFormikContext<IFormCreationValues>();

  const venueTypeId = useMemo(() => {
    return selectedVenueId
      ? existingVenueDetail?.venueTypeId
      : values.venue.venueType?.id;
  }, [
    existingVenueDetail?.venueTypeId,
    selectedVenueId,
    values.venue.venueType?.id,
  ]);

  const defaultLocation = useMemo(() => {
    return selectedVenueId
      ? existingVenueCoordinates
      : {
          latitude: values.venue?.address?.location?.lat ?? 0.0,
          longitude: values.venue?.address?.location?.lng ?? 0.0,
        };
  }, [
    existingVenueCoordinates,
    selectedVenueId,
    values.venue?.address?.location?.lat,
    values.venue?.address?.location?.lng,
  ]);

  // Update default location for station's position
  useEffect(() => {
    if (
      !values.placement.location ||
      (values.placement.location.latitude === 0.0 &&
        values.placement.location.longitude === 0.0)
    ) {
      setFieldValue('placement.location', defaultLocation);
    }
  }, [defaultLocation, setFieldValue, values.placement.location]);

  // Properties for SelectDistributionChannelStep
  const onChangeDistributionChannel = useCallback(
    (value: DistributionChannel | null) => {
      setFieldValue('placement.distributionChannel', value);
    },
    [setFieldValue],
  );

  const handleGoToNextStep = useCallback(() => {
    switch (pinningStep) {
      case PINNING_STEP.ASSIGN_STATION_UPLOAD_PHOTOS: {
        const resolvedStation = isRelocationFlow
          ? ({
              id: assignedStationId || values.placement.assignedStationId || '',
              name: '',
              imei:
                assignedStationImei ||
                values.placement.assignedStationImei ||
                '',
              serialNumber: values.placement.assignedStationSerialNumber || '',
              type: assignedStationType || values.placement.assignedStationType,
            } as StationFilterOption)
          : values.placement.station;
        handleUpdatePinningFormData({
          ...values,
          placement: {
            ...values.placement,
            isRelocationFlow,
            station: resolvedStation,
          },
        });
        break;
      }
      case getNaturalPreviousStep(
        PINNING_STEP.ASSIGN_STATION_ADD_DISTRIBUTION_CHANNEL,
      ):
        // automatically set distribution channel = enterprise for distributor & partner users
        if (
          checkRolesMatchWithPrimaryUserRole([
            USER_ROLE.DISTRIBUTOR,
            USER_ROLE.LIMITED_DISTRIBUTOR,
            USER_ROLE.PARTNER,
          ])
        ) {
          onChangeDistributionChannel(
            distributionChannelOptions.find(
              (channel) => channel.name.trim().toLowerCase() == 'enterprise',
            ) ?? null,
          );
        }
        break;
    }
    goToNextStep();
  }, [
    assignedStationId,
    assignedStationImei,
    assignedStationType,
    distributionChannelOptions,
    goToNextStep,
    handleUpdatePinningFormData,
    isRelocationFlow,
    onChangeDistributionChannel,
    pinningStep,
    values,
  ]);
  const stationInfo = useMemo(() => {
    const stationType = isRelocationFlow
      ? assignedStationType || values.placement.assignedStationType
      : values.placement.station?.type;
    return getStationInfoDisplay(stationType as STATION_TYPE);
  }, [
    assignedStationType,
    isRelocationFlow,
    values.placement.assignedStationType,
    values.placement.station?.type,
  ]);

  const onChangeStation = (
    _: React.SyntheticEvent,
    value: StationFilterOption | null,
  ) => {
    setFieldValue('placement.station', value);
  };

  // Properties for PinCoordinatesStep
  const onMarkerDragEnd = (e: google.maps.MapMouseEvent) => {
    const lat = e.latLng?.lat() ?? 0;
    const lng = e.latLng?.lng() ?? 0;
    setFieldValue('placement.location.latitude', lat);
    setFieldValue('placement.location.longitude', lng);
    setHasPinMoved(true);
  };
  const onChangeCurrentLocation = (coordinates: ICoordinate) => {
    setFieldValue('placement.location.latitude', coordinates.latitude);
    setFieldValue('placement.location.longitude', coordinates.longitude);
  };

  /**
   * Each of these writes per placement as well as globally, so a batch that moves
   * several stations carries the reason and note each one was given.
   *
   * Memoised because the confirm steps preselect the first reason from an effect
   * that lists the handler in its dependencies.
   */
  const onChangeSwapReasonId = useCallback(
    (value: string) => {
      setFieldValue('swapReasonId', value);
      setFieldValue('placement.swapReasonId', value);
    },
    [setFieldValue],
  );

  const onChangeSwapNote = useCallback(
    (value: string) => {
      setFieldValue('swapNote', value);
      setFieldValue('placement.swapNote', value);
    },
    [setFieldValue],
  );

  const onChangeRelocationReasonId = useCallback(
    (value: string) => {
      setFieldValue('relocationReasonId', value);
      setFieldValue('placement.relocationReasonId', value);
    },
    [setFieldValue],
  );

  const onChangeRelocationNote = useCallback(
    (value: string) => {
      setFieldValue('relocationNote', value);
      setFieldValue('placement.relocationNote', value);
    },
    [setFieldValue],
  );

  const hasRelocationPhotosUploaded = useMemo(() => {
    if (!isRelocationFlow) return false;
    const attachment = values.placement.attachment;
    return !!(
      attachment?.imei ||
      attachment?.fullStationScreen ||
      attachment?.stationLeftSide ||
      attachment?.stationRightSide ||
      attachment?.stationFrontSide ||
      attachment?.stationPerspective
    );
  }, [isRelocationFlow, values.placement.attachment]);

  // Properties for UploadPhotosStep
  const initialAttachment = useMemo(() => {
    return {
      [ATTACHMENT_FIELD_NAME.imei]: {
        url: values.placement.attachment?.imei,
        fileName: ATTACHMENT_FIELD_NAME.imei,
      },
      [ATTACHMENT_FIELD_NAME.fullStationScreen]: {
        url: values.placement.attachment?.fullStationScreen,
        fileName: ATTACHMENT_FIELD_NAME.fullStationScreen,
      },
      [ATTACHMENT_FIELD_NAME.stationPerspective]: {
        url: values.placement.attachment?.stationPerspective,
        fileName: ATTACHMENT_FIELD_NAME.stationPerspective,
      },
      [ATTACHMENT_FIELD_NAME.stationLeftSide]: {
        url: values.placement.attachment?.stationLeftSide,
        fileName: ATTACHMENT_FIELD_NAME.stationLeftSide,
      },
      [ATTACHMENT_FIELD_NAME.stationRightSide]: {
        url: values.placement.attachment?.stationRightSide,
        fileName: ATTACHMENT_FIELD_NAME.stationRightSide,
      },
      [ATTACHMENT_FIELD_NAME.stationFrontSide]: {
        url: values.placement.attachment?.stationFrontSide,
        fileName: ATTACHMENT_FIELD_NAME.stationFrontSide,
      },
      [ATTACHMENT_FIELD_NAME.additional]: {
        url: values.placement.attachment?.additional,
        fileName: ATTACHMENT_FIELD_NAME.additional,
      },
    };
  }, [
    values.placement.attachment?.additional,
    values.placement.attachment?.fullStationScreen,
    values.placement.attachment?.imei,
    values.placement.attachment?.stationFrontSide,
    values.placement.attachment?.stationLeftSide,
    values.placement.attachment?.stationPerspective,
    values.placement.attachment?.stationRightSide,
  ]);

  const handleUploadPhoto =
    (fieldName: ATTACHMENT_FIELD_NAME) => (file: IPreviewFile) => {
      setFieldValue(fieldName, file.url);
    };

  const handleRemovePhoto = (fieldName: ATTACHMENT_FIELD_NAME) => () => {
    setFieldValue(fieldName, '');
  };

  const onSelectPlacementCallback = (
    placement: StationPlacementType | null,
  ) => {
    setFieldValue('placement.station', null);
    setFieldValue('placement.placementId', placement?.id || null);
    setFieldValue('placement.name', placement?.name || '');
    setFieldValue(
      'placement.placementType',
      placement?.placementType
        ? ({
            id: placement.placementTypeId,
            name: placement.placementType,
          } as PlacementType)
        : null,
    );
    setFieldValue(
      'placement.distributionChannel',
      placement?.distributionChannel
        ? ({
            id: placement.distributionChannelId,
            name: placement.distributionChannel,
          } as DistributionChannel)
        : null,
    );
    setFieldValue(
      'placement.isPlacementAssignedStation',
      placement?.type !== undefined,
    );
    setFieldValue('placement.assignedStationId', placement?.stationId);
    setFieldValue('placement.assignedStationImei', placement?.imei);
    setFieldValue('placement.assignedStationType', placement?.type);
    setFieldValue(
      'placement.assignedStationSerialNumber',
      placement?.serialNumber,
    );

    // Pre-fill coordinates for relocation if available
    if (placement?.latitude != null && placement?.longitude != null) {
      setFieldValue('placement.location.latitude', placement.latitude);
      setFieldValue('placement.location.longitude', placement.longitude);
    }
  };

  useEffect(() => {
    switch (pinningStep) {
      case PINNING_STEP.ASSIGN_STATION_SCAN_QR:
        // The swap reason and note are left alone: this step is reached again by
        // stepping back or adding a placement, and clearing them here wiped the
        // reason an earlier swap was given. Picking a placement resets them.
        setCurrentFields(['placement.station']);
        return;
      case PINNING_STEP.ASSIGN_STATION_SELECT_STATION:
        setCurrentFields(['placement.station']);
        return;
      case PINNING_STEP.ASSIGN_STATION_ADD_PLACEMENT_NAME:
        setCurrentFields(['placement.name']);
        return;
      case PINNING_STEP.ASSIGN_STATION_ADD_PLACEMENT_TYPE:
        setCurrentFields(['placement.placementType']);
        return;
      case PINNING_STEP.ASSIGN_STATION_ADD_DISTRIBUTION_CHANNEL:
        setCurrentFields(['placement.distributionChannel']);
        return;
      case PINNING_STEP.CONFIRM_SWAP_STATION:
        setCurrentFields(['swapReasonId']);
        return;
      case PINNING_STEP.CONFIRM_RELOCATION:
        setCurrentFields(['relocationReasonId']);
        setHasPinMoved(false);
        return;
      case PINNING_STEP.ASSIGN_STATION_PIN_COORDINATES:
        setCurrentFields([
          'placement.location.latitude',
          'placement.location.longitude',
        ]);
        return;
      case PINNING_STEP.ASSIGN_STATION_UPLOAD_PHOTOS:
        setCurrentFields([
          'placement.attachment.imei',
          'placement.attachment.fullStationScreen',
          'placement.attachment.stationLeftSide',
          'placement.attachment.stationRightSide',
          'placement.attachment.stationFrontSide',
          'placement.attachment.stationPerspective',
        ]);
        return;
      default:
        return;
    }
  }, [pinningStep, setFieldValue]);

  const areFieldsComplete = useMemo(() => {
    // Check if all fields in currentFields have values and no errors in the formik values
    const fieldsValid = currentFields.every((field) => {
      // Extract the value from formik.values
      const fieldValue = field.split('.').reduce(
        (acc, key) => acc?.[key as keyof typeof acc],
        values as unknown, // formik.values here
      );

      // Extract the error from formik.errors
      const fieldError = field.split('.').reduce(
        (acc, key) => acc?.[key as keyof typeof acc],
        errors as unknown, // formik.errors here
      );

      // Check if the field has a value and no associated errors
      return !!fieldValue && !fieldError;
    });

    // For relocation, coordinates step requires the pin to have been moved
    if (
      isRelocationFlow &&
      pinningStep === PINNING_STEP.ASSIGN_STATION_PIN_COORDINATES
    ) {
      return fieldsValid && hasPinMoved;
    }

    return fieldsValid;
  }, [
    currentFields,
    errors,
    hasPinMoved,
    isRelocationFlow,
    pinningStep,
    values,
  ]);

  const buttonText = stepButtonLabelMap[pinningStep] ?? 'Proceed to review';

  return (
    <>
      <PaddingBox
        flex={1}
        display="flex"
        sx={{
          bgcolor: 'background.paper',
          width: '100%',
          height: '100%',
        }}
      >
        <Box
          flex={1}
          display="flex"
          flexDirection="column"
          gap={2}
          width="100%"
        >
          {pinningStep === PINNING_STEP.ASSIGN_STATION_SCAN_QR && (
            <ScanQRStep />
          )}

          {pinningStep === PINNING_STEP.ASSIGN_STATION_SELECT_STATION && (
            <SelectStationStep
              station={values.placement.station}
              onChangeStation={onChangeStation}
            />
          )}

          {pinningStep === PINNING_STEP.ASSIGN_STATION_ADD_PLACEMENT_NAME && (
            <AddPlacementNameStep
              stationImgUrl={stationInfo?.imageUrl}
              stationTitle={stationInfo?.title}
              stationImei={
                isRelocationFlow
                  ? assignedStationImei || values.placement.assignedStationImei
                  : values.placement.station?.imei
              }
            />
          )}

          {pinningStep === PINNING_STEP.ASSIGN_STATION_ADD_PLACEMENT_TYPE && (
            <SelectPlacementTypeStep venueTypeId={venueTypeId} />
          )}

          {pinningStep ===
            PINNING_STEP.ASSIGN_STATION_ADD_DISTRIBUTION_CHANNEL && (
            <SelectDistributionChannelStep
              distributionChannel={values.placement.distributionChannel}
              onChangeDistributionChannel={onChangeDistributionChannel}
            />
          )}

          {pinningStep ===
            PINNING_STEP.ASSIGN_STATION_SELECT_EXISTING_PLACEMENT && (
            <SelectExistingPlacementStep
              placements={existingPlacements}
              onSearchPlacement={onSearchExistingPlacement}
              onSelectPlacementCallback={onSelectPlacementCallback}
              isLoadingPlacements={isLoadingPlacements}
              currentPlacementListInPinningForm={placementListInfo
                .map((e) => e.placementId || '')
                .filter(
                  (e) =>
                    e !== '' &&
                    (isAddingFromReview || e !== selectedPlacementId),
                )}
            />
          )}

          {pinningStep === PINNING_STEP.ASSIGN_STATION_PIN_COORDINATES && (
            <GeolocationProvider>
              <PinCoordinatesStep
                onMarkerDragEnd={onMarkerDragEnd}
                onChangeCurrentLocation={onChangeCurrentLocation}
                latitude={values.placement.location.latitude}
                longitude={values.placement.location.longitude}
              />
            </GeolocationProvider>
          )}

          {pinningStep === PINNING_STEP.ASSIGN_STATION_UPLOAD_PHOTOS && (
            <UploadPhotosStep
              initAttachmentFiles={
                isRelocationFlow && !hasRelocationPhotosUploaded
                  ? undefined
                  : initialAttachment
              }
              handleUploadPhoto={handleUploadPhoto}
              handleRemovePhoto={handleRemovePhoto}
              imei={
                isRelocationFlow
                  ? assignedStationImei ?? ''
                  : values.placement.station?.imei ?? ''
              }
            />
          )}

          {pinningStep === PINNING_STEP.CONFIRM_SWAP_STATION && (
            <ConfirmSwapStation
              currentStationImei={
                assignedStationImei || values.placement.assignedStationImei
              }
              currentStationType={
                (assignedStationType ||
                  values.placement.assignedStationType) as STATION_TYPE
              }
              newStationImei={values.placement.station?.imei ?? ''}
              newStationType={values.placement.station?.type as STATION_TYPE}
              swapReasonId={values.swapReasonId}
              onChangeSwapReasonId={onChangeSwapReasonId}
              swapNote={values.swapNote}
              onChangeSwapNote={onChangeSwapNote}
            />
          )}

          {pinningStep === PINNING_STEP.ASSIGN_STATION_SELECT_ACTION && (
            <SelectActionStep />
          )}

          {pinningStep === PINNING_STEP.CONFIRM_RELOCATION && (
            <ConfirmRelocationStep
              stationImei={
                assignedStationImei || values.placement.assignedStationImei
              }
              stationType={
                (assignedStationType ||
                  values.placement.assignedStationType) as STATION_TYPE
              }
              relocationReasonId={values.relocationReasonId}
              onChangeRelocationReasonId={onChangeRelocationReasonId}
              relocationNote={values.relocationNote}
              onChangeRelocationNote={onChangeRelocationNote}
            />
          )}
        </Box>
      </PaddingBox>
      {![
        PINNING_STEP.ASSIGN_STATION_SCAN_QR,
        PINNING_STEP.ASSIGN_STATION_SELECT_EXISTING_PLACEMENT,
        PINNING_STEP.ASSIGN_STATION_SELECT_ACTION,
      ].includes(pinningStep) && (
        <PaddingBox
          bgcolor="background.paper"
          alignItems="center"
          position={'fixed'}
          sx={{ bottom: 0, left: 0 }}
          width={'100%'}
        >
          <Button
            fullWidth
            size="large"
            variant="contained"
            style={{ borderRadius: '100px' }}
            disabled={!areFieldsComplete}
            onClick={handleGoToNextStep}
          >
            <Typography variant="body2" fontWeight={500}>
              {buttonText}
            </Typography>
          </Button>
        </PaddingBox>
      )}
    </>
  );
};
